import React, { useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService } from '../../../../services/worker'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import {
  MobileCard,
  MobileEmptyState,
  MobileSecondaryButton,
  MobileSectionTitle,
} from './WorkerMobilePrimitives'

/** Availability & time off, matching the Android Availability tab. */

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const WorkerMobileAvailability: React.FC<{ state: WorkerWorkspaceState }> = ({ state }) => {
  const { t, formatDateTime } = useI18n()
  const { data, busy, run } = state
  const [tab, setTab] = useState<'usual' | 'off'>('usual')
  const [window, setWindow] = useState({ weekday: '1', startTime: '08:00', endTime: '17:00' })
  const [timeOff, setTimeOff] = useState({ startsAt: '', endsAt: '', reason: '' })

  return (
    <div className="wm-screen" data-testid="worker-mobile-availability">
      <MobileSectionTitle
        title={t('Availability')}
        detail={t('Set your usual week and time off. Assigned shifts still take priority.')}
      />
      <div className="wm-segmented" role="tablist" aria-label={t('Availability')}>
        <button type="button" role="tab" aria-selected={tab === 'usual'} className="wm-segment" data-active={tab === 'usual' || undefined} onClick={() => setTab('usual')}>{t('Usual Week')}</button>
        <button type="button" role="tab" aria-selected={tab === 'off'} className="wm-segment" data-active={tab === 'off' || undefined} onClick={() => setTab('off')}>{t('Time Off')}</button>
      </div>

      {tab === 'usual' ? (
        <>
          <MobileCard>
            <form
              className="wm-form"
              onSubmit={(event) => {
                event.preventDefault()
                void run('availability', () => workerService.createAvailability({
                  weekday: Number(window.weekday),
                  startTime: window.startTime,
                  endTime: window.endTime,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }), t('Availability saved.'))
              }}
            >
              <label className="wm-field">
                <span>{t('Day')}</span>
                <select value={window.weekday} onChange={(event) => setWindow({ ...window, weekday: event.target.value })}>
                  {WEEKDAYS.map((day, index) => <option value={index} key={day}>{t(day)}</option>)}
                </select>
              </label>
              <div className="wm-field-row">
                <label className="wm-field"><span>{t('Start')}</span><input type="time" value={window.startTime} onChange={(event) => setWindow({ ...window, startTime: event.target.value })} /></label>
                <label className="wm-field"><span>{t('End')}</span><input type="time" value={window.endTime} onChange={(event) => setWindow({ ...window, endTime: event.target.value })} /></label>
              </div>
              <button type="submit" className="wm-button wm-button--primary" data-tone="info" disabled={Boolean(busy)}>{t('Save window')}</button>
            </form>
          </MobileCard>
          {data.availability.length ? data.availability.map((row: any) => (
            <MobileCard key={row.id}>
              <div className="wm-card__heading">
                <div>
                  <strong>{row.weekday == null ? t('One-time window') : t(WEEKDAYS[row.weekday])}</strong>
                  <small>{row.start_time || row.starts_at} – {row.end_time || row.ends_at}</small>
                </div>
              </div>
              <MobileSecondaryButton
                label={t('Remove')}
                disabled={Boolean(busy)}
                onClick={() => void run(`availability-${row.id}`, () => workerService.deleteAvailability(row.id), t('Availability removed.'))}
              />
            </MobileCard>
          )) : <MobileEmptyState title={t('No usual week set')} detail={t('Add the hours you can normally work.')} />}
        </>
      ) : (
        <>
          <MobileCard>
            <form
              className="wm-form"
              onSubmit={(event) => {
                event.preventDefault()
                void run('time-off', () => workerService.createUnavailability({
                  startsAt: new Date(timeOff.startsAt).toISOString(),
                  endsAt: new Date(timeOff.endsAt).toISOString(),
                  reason: timeOff.reason,
                }), t('Time off saved.'))
              }}
            >
              <label className="wm-field"><span>{t('Starts')}</span><input required type="datetime-local" value={timeOff.startsAt} onChange={(event) => setTimeOff({ ...timeOff, startsAt: event.target.value })} /></label>
              <label className="wm-field"><span>{t('Ends')}</span><input required type="datetime-local" value={timeOff.endsAt} onChange={(event) => setTimeOff({ ...timeOff, endsAt: event.target.value })} /></label>
              <label className="wm-field"><span>{t('Reason')}</span><input value={timeOff.reason} onChange={(event) => setTimeOff({ ...timeOff, reason: event.target.value })} /></label>
              <button type="submit" className="wm-button wm-button--primary" data-tone="info" disabled={Boolean(busy)}>{t('Add time off')}</button>
            </form>
          </MobileCard>
          {data.unavailability.length ? data.unavailability.map((row: any) => (
            <MobileCard key={row.id}>
              <div className="wm-card__heading">
                <div>
                  <strong>{row.reason || t('Unavailable')}</strong>
                  <small>{formatDateTime(row.starts_at, { dateStyle: 'short', timeStyle: 'short' })} – {formatDateTime(row.ends_at, { dateStyle: 'short', timeStyle: 'short' })}</small>
                </div>
              </div>
              <MobileSecondaryButton
                label={t('Remove')}
                disabled={Boolean(busy)}
                onClick={() => void run(`off-${row.id}`, () => workerService.deleteUnavailability(row.id), t('Time off removed.'))}
              />
            </MobileCard>
          )) : <MobileEmptyState title={t('No time off booked')} detail={t('Add dates you cannot work.')} />}
        </>
      )}
    </div>
  )
}
